import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

import { getMcapSchoolView } from "api/api.service";
import typography from "../typography";

/**
 * The two panels a single school earns that a district roll-up cannot show.
 *
 * Rendered only when a school is selected. At district scope a playback funnel
 * across every school answers nothing an administrator can act on, and the
 * activity table would run to hundreds of rows — so with no school picked the
 * dashboard stays exactly as it was.
 *
 * Both are drawn, not written: a funnel as proportional bars, an activity
 * breakdown as a ranked bar table. The numbers are still there to read, but
 * nobody has to read them to see the shape.
 */

const panelVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.23, 1, 0.32, 1] } },
};

const STEP_LABELS = {
  START: "Started",
  PERCENT_25: "A quarter through",
  PERCENT_50: "Halfway",
  PERCENT_75: "Three quarters",
  END: "Finished",
};

// One ramp, darkening toward completion — the further through, the stronger
// the colour. Not a traffic light: dropping off halfway through a calming
// video is not an error.
const STEP_COLORS = ["#9FC7B4", "#7BB79B", "#559C7C", "#358A63", "#227F5D"];

const KIND_LABELS = { VIDEO: "Video", AUDIO: "Audio", BUILT_IN: "Built-in" };

export default function SchoolDetailPanels({ schoolId, filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("resets");

  useEffect(() => {
    if (!schoolId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    getMcapSchoolView({ ...filters, schoolId }, controller.signal)
      .then((res) => setData(res || null))
      .catch((e) => {
        if (e?.name !== "AbortError" && e?.name !== "CanceledError") setData(null);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, JSON.stringify(filters)]);

  const funnel = data?.playbackFunnel ?? [];
  const started = data?.playbackSessions ?? 0;

  const activities = useMemo(() => {
    const rows = [...(data?.activities ?? [])];
    if (sortBy === "classrooms") return rows.sort((a, b) => b.classrooms - a.classrooms);
    if (sortBy === "saved") return rows.sort((a, b) => (b.timeSavedMinutes ?? -1) - (a.timeSavedMinutes ?? -1));
    return rows.sort((a, b) => b.resets - a.resets);
  }, [data, sortBy]);

  const maxResets = activities.reduce((m, r) => Math.max(m, r.resets), 0) || 1;

  // Share of started sessions that reached the end — the headline the five
  // funnel rows are the working for.
  const completionRate = funnel.find((f) => f.step === "END")?.pctOfStarts ?? 0;

  if (!schoolId) return null;

  return (
    <motion.div initial="hidden" animate="visible" variants={panelVariants} className="mcap-school-panels">
      <div className="mcap-impl-card">
        <div className="mcap-impl-card-head">
          <div>
            <h5 className="mcap-impl-card-title mb-0">{typography.PLAYBACK_TITLE}</h5>
            <p className="standard-typo-12-400-ld mb-0">{typography.PLAYBACK_DETAIL}</p>
          </div>
          {!loading && started > 0 && <span className="standard-typo-12-400-ld">{started} started</span>}
        </div>

        {!loading && started === 0 ? (
          <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.PLAYBACK_EMPTY}</p>
        ) : (
          <>
            {/* The one number this panel exists to produce, stated once at the
                top. Five short rows in a card sized by its neighbour left a
                third of it empty; this fills that space with the answer rather
                than with padding. */}
            <div className="mcap-funnel-hero">
              <p className="mcap-funnel-hero-value">
                {completionRate}
                <span>%</span>
              </p>
              <p className="mcap-funnel-hero-label">{typography.PLAYBACK_COMPLETION}</p>
            </div>

            <ul className="mcap-funnel">
              {funnel.map((step, i) => (
                <li key={step.step}>
                  <span className="mcap-funnel-label">{STEP_LABELS[step.step] || step.step}</span>
                  <span className="mcap-funnel-track">
                    <span
                      className="mcap-funnel-fill"
                      style={{ width: `${step.pctOfStarts}%`, backgroundColor: STEP_COLORS[i] }}
                      title={`${step.sessions} of ${started} sessions`}
                    />
                  </span>
                  <span className="mcap-funnel-pct">{step.pctOfStarts}%</span>
                  <span className="mcap-funnel-n">{step.sessions}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="mcap-impl-card mcap-school-activities">
        <div className="mcap-impl-card-head">
          <div>
            <h5 className="mcap-impl-card-title mb-0">{typography.ACTIVITIES_TITLE}</h5>
            <p className="standard-typo-12-400-ld mb-0">{typography.ACTIVITIES_DETAIL}</p>
          </div>
          <div className="mcap-sort">
            <span className="standard-typo-12-400-ld">Sort</span>
            {[
              { key: "resets", label: "Resets" },
              { key: "classrooms", label: "Classrooms" },
              { key: "saved", label: "Time saved" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`mcap-sort-btn${sortBy === key ? " is-active" : ""}`}
                onClick={() => setSortBy(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {!loading && activities.length === 0 ? (
          <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.ACTIVITIES_EMPTY}</p>
        ) : (
          <table className="mcap-moments mcap-activities">
            <thead>
              <tr>
                <th>Activity</th>
                <th className="mcap-moments-barcol">Resets</th>
                <th className="text-end">Classrooms</th>
                <th className="text-end">Usefulness</th>
                <th className="text-end">Time saved</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((row) => (
                <tr key={row.key}>
                  <td className="mcap-moments-name">
                    {row.title}
                    <span className="mcap-kind">{KIND_LABELS[row.kind] || row.kind}</span>
                  </td>
                  <td className="mcap-moments-barcol">
                    <span className="mcap-moments-bar">
                      <span className="mcap-moments-bar-fill" style={{ width: `${(row.resets / maxResets) * 100}%` }} />
                    </span>
                    <span className="mcap-moments-bar-num">{row.resets}</span>
                  </td>
                  <td className="text-end mcap-moments-num">{row.classrooms}</td>
                  <td className="text-end mcap-moments-num">
                    {row.avgUsefulness === null ? (
                      <span className="mcap-moments-muted">—</span>
                    ) : (
                      <span className="mcap-rating">
                        {row.avgUsefulness}
                        {/* Teacher-reported usefulness is a 1-10 scale — see
                            LogClassActivityOutcomeRequest. This read "/5". */}
                        <em>/10</em>
                      </span>
                    )}
                  </td>
                  <td className="text-end mcap-moments-num">
                    {row.timeSavedMinutes === null ? (
                      <span className="mcap-moments-muted">—</span>
                    ) : (
                      <>
                        {row.timeSavedMinutes}
                        <em className="mcap-unit"> min</em>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && activities.length > 0 && (
          <p className="mcap-table-foot">
            A dash means fewer than {data.aggregationThreshold} records — too few to report.
          </p>
        )}
      </div>
    </motion.div>
  );
}
